import type {
  ScheduleEvent,
  ScheduleConfig,
  Resource,
  CustomAttributeDefinition,
} from '@fulcrum/schedule-embedded';
import { DEMO_CLIENTS } from './mockData';

function weekDay(dayOffset: number, h: number, m = 0): Date {
  const d = new Date();
  const dow = d.getDay();
  const daysToMon = dow === 0 ? -6 : 1 - dow;
  const result = new Date(d);
  result.setDate(d.getDate() + daysToMon + dayOffset);
  result.setHours(h, m, 0, 0);
  return result;
}

// ─── Behavioral Health Industry ────────────────────────────────────────────────
// Provider IDs match DEMO_PROVIDER_PROFILES in App.tsx so AI matching works in BH mode.

export const BH_PROVIDERS: Resource[] = [
  { id: 'alice',  name: 'Alice Johnson, LPC',     color: '#8B5CF6', customProperties: { specialty: 'Trauma / Adolescent',    credential: 'LPC'  } },
  { id: 'bob',    name: 'Robert Martinez, LCSW',  color: '#10B981', customProperties: { specialty: 'Addiction / Adult',       credential: 'LCSW' } },
  { id: 'carol',  name: 'Carol Kim, PhD',          color: '#F59E0B', customProperties: { specialty: 'Trauma / PTSD',           credential: 'PhD'  } },
  { id: 'dave',   name: 'David Chen, LMFT',        color: '#EF4444', customProperties: { specialty: 'Adolescent / Family',     credential: 'LMFT' } },
  { id: 'eve',    name: 'Eva Wilson, LPCC',         color: '#06B6D4', customProperties: { specialty: 'Depression / Anxiety',   credential: 'LPCC' } },
  { id: 'frank',  name: 'Frank Torres, LCSW',      color: '#EC4899', customProperties: { specialty: 'Trauma / Addiction',      credential: 'LCSW' } },
  { id: 'grace',  name: 'Grace Lee, LMFT',          color: '#84CC16', customProperties: { specialty: 'Child / Adolescent',    credential: 'LMFT' } },
  { id: 'henry',  name: 'Henry Park, PhD',          color: '#F97316', customProperties: { specialty: 'OCD / Anxiety',         credential: 'PhD'  } },
  { id: 'iris',   name: 'Iris Nakamura, LPC',       color: '#A78BFA', customProperties: { specialty: 'Trauma / Women',        credential: 'LPC'  } },
  { id: 'jack',   name: 'Jack Thompson, LMFT',      color: '#34D399', customProperties: { specialty: 'Couples / Family',      credential: 'LMFT' } },
];

export const BH_CUSTOM_ATTRS: CustomAttributeDefinition[] = [
  {
    key: 'sessionType',
    label: 'Session Type',
    type: 'select',
    options: [
      { value: 'individual', label: 'Individual Therapy' },
      { value: 'intake',     label: 'Intake / Assessment' },
      { value: 'group',      label: 'Group Therapy'       },
      { value: 'couples',    label: 'Couples Therapy'     },
      { value: 'family',     label: 'Family Therapy'      },
      { value: 'crisis',     label: 'Crisis Intervention' },
    ],
  },
  {
    key: 'modality',
    label: 'Modality',
    type: 'select',
    options: [
      { value: 'CBT',  label: 'CBT'         },
      { value: 'DBT',  label: 'DBT'         },
      { value: 'EMDR', label: 'EMDR'        },
      { value: 'ACT',  label: 'ACT'         },
      { value: 'ERP',  label: 'ERP'         },
      { value: 'EFT',  label: 'EFT'         },
      { value: 'play', label: 'Play Therapy' },
    ],
  },
  {
    key: 'visitType',
    label: 'Visit Type',
    type: 'select',
    options: [
      { value: 'in-person',  label: 'In-Person'  },
      { value: 'telehealth', label: 'Telehealth' },
    ],
  },
];


const SESSION_MODALITIES: Record<string, string> = {
  alice:  'EMDR',
  bob:    'DBT',
  carol:  'EMDR',
  dave:   'CBT',
  eve:    'CBT',
  frank:  'DBT',
  grace:  'play',
  henry:  'ERP',
  iris:   'EMDR',
  jack:   'EFT',
};

export function generateBhEvents(): ScheduleEvent[] {
  const events: ScheduleEvent[] = [];
  let clientIdx = 0;
  let evIdx = 0;

  const nextClient = () => DEMO_CLIENTS[clientIdx++ % DEMO_CLIENTS.length];

  // Standard individual therapy slots (50 min, starting on the hour)
  const stdSlots: [number, number][] = [
    [9, 0], [10, 0], [11, 0], [13, 0], [14, 0], [15, 0], [16, 0],
  ];

  // Lighter schedule — some providers have intakes or shorter days
  const lightSlotSets: [number, number][][] = [
    [[9, 0], [10, 0], [13, 0], [14, 0], [15, 0]],
    [[9, 0], [11, 0], [13, 0], [15, 0]],
    [[10, 0], [11, 0], [14, 0], [15, 0], [16, 0]],
    [[9, 0], [10, 0], [13, 0], [14, 0]],
    [[9, 0], [11, 0], [14, 0], [16, 0]],
  ];

  const busyProviders  = BH_PROVIDERS.slice(0, 5);
  const lightProviders = BH_PROVIDERS.slice(5);

  for (let day = 0; day < 5; day++) {
    for (const provider of busyProviders) {
      for (const [h, m] of stdSlots) {
        const client = nextClient();
        const isIntake = clientIdx % 9 === 0;
        const isTelehealth = clientIdx % 4 === 0;
        const modality = SESSION_MODALITIES[provider.id] ?? 'CBT';
        evIdx++;
        const inPersonRooms = ['Room 101 — Annex Building', 'Room 204 — Main Campus', 'Room 310 — West Wing'];
        events.push({
          id: `bh-${evIdx}`,
          title: client.name,
          clientId: client.id,
          startUtc: weekDay(day, h, m).toISOString(),
          endUtc:   weekDay(day, h, m + (isIntake ? 90 : 50)).toISOString(),
          resourceId: provider.id,
          color: provider.color,
          backgroundColor: provider.color,
          location: isTelehealth ? 'Telehealth — Zoom' : inPersonRooms[evIdx % inPersonRooms.length],
          customAttributes: [
            { key: 'sessionType', value: isIntake ? 'intake' : 'individual' },
            { key: 'modality',    value: modality },
            { key: 'visitType',   value: isTelehealth ? 'telehealth' : 'in-person' },
          ],
        });
      }
    }

    lightProviders.forEach((provider, pi) => {
      const slots = lightSlotSets[pi % lightSlotSets.length];
      for (const [h, m] of slots) {
        const client = nextClient();
        const isCouples = provider.id === 'jack';
        const isFamily  = provider.id === 'dave' && clientIdx % 3 === 0;
        const isTelehealth = clientIdx % 5 === 0;
        const modality = SESSION_MODALITIES[provider.id] ?? 'CBT';
        const sessionType = isCouples ? 'couples' : isFamily ? 'family' : 'individual';
        evIdx++;
        const inPersonRoomsLight = ['Room 101 — Annex Building', 'Room 204 — Main Campus', 'Room 310 — West Wing', 'Chapel Hall — Ground Floor'];
        events.push({
          id: `bh-${evIdx}`,
          title: client.name,
          clientId: client.id,
          startUtc: weekDay(day, h, m).toISOString(),
          endUtc:   weekDay(day, h, m + (isCouples || isFamily ? 60 : 50)).toISOString(),
          resourceId: provider.id,
          color: provider.color,
          backgroundColor: provider.color,
          location: isTelehealth ? 'Telehealth — Zoom' : inPersonRoomsLight[evIdx % inPersonRoomsLight.length],
          customAttributes: [
            { key: 'sessionType', value: sessionType },
            { key: 'modality',    value: modality },
            { key: 'visitType',   value: isTelehealth ? 'telehealth' : 'in-person' },
          ],
        });
      }
    });
  }

  return events;
}

export const BH_LOCATIONS: string[] = [
  'Room 101 — Annex Building',
  'Room 106 — Hybrid / Zoom',
  'Room 204 — Main Campus',
  'Room 310 — West Wing',
  'Chapel Hall — Ground Floor',
  'Telehealth — Zoom',
];

export function getBhConfig(): ScheduleConfig {
  return {
    timezone: 'America/Chicago',
    view: 'weekdays',
    timeRange: { start: '08:00', end: '18:00' },
    slotDuration: 60,
    workingHours: { start: '09:00', end: '17:00', daysOfWeek: [1, 2, 3, 4, 5] },
    allowEventCreation: true,
    resourceGrouping: 'byDate',
    customAttributeDefinitions: BH_CUSTOM_ATTRS,
    showAllDaySection: false,
    timeFormat: '12h',
    locations: BH_LOCATIONS,
  };
}

